import {
  envIsIntegration,
  envIsLocal,
  envIsProd,
  envIsPublicCloud,
} from '@wandb/common/config';
import {
  ALL_BETA_FEATURE_KEYS,
  BetaFeature,
  BetaFeatureKey,
} from '@wandb/common/util/betaFeatures';
import {useCallback, useEffect, useMemo} from 'react';

import {
  useUpdateUserInfoMutation,
  useViewerUserInfoQuery,
} from '../generated/graphql';
import {Analytics} from '../services/analytics';
import {useViewer} from '../state/viewer/hooks';
import {UserInfo} from '../types/graphql';
import {viewingAs} from './admin';
import {useFeatureFlagPanelSearchBeta} from './useFeatureFlag';

export function useBetaFeatureUnicornModeEnabled(): boolean {
  return useBetaFeature('unicorn-plot').isEnabled;
}

export function useBetaFeatureModelRegistryEnabled(): boolean {
  return useBetaFeature('model-registry').isEnabled;
}

export function useBetaFeatureNightModeEnabled(): boolean {
  return useBetaFeature('night').isEnabled;
}

export function useBetaFeatureVega3Enabled(): boolean {
  return useBetaFeature('vega3').isEnabled;
}

export function useBetaFeatureWeave1(): boolean | undefined {
  const ff = useBetaFeature('weave1');
  const isWandbEmployee = useViewerIsWandbEmployee();

  // TODO: some non-employees have weave1 explicitly set to false,
  // invalidating the logic below. This is a temporary fix to prevent
  // those users from being unable to force-bucket into weave1. We should
  // remove this once we have a better solution.
  if (!isWandbEmployee) {
    return undefined;
  }

  // Here we use the userSelectedValue instead of isEnabled because we want to
  // return undefined if the user has not selected a value yet.
  // This is because we want to let experiment bucketing decide whether a user
  // should get weave1 if they have not explicitly opted in or out. If they
  // have explicitly opted in or out, we want to respect that.
  if (envIsProd) {
    return ff.userSelectedValue;
  }
  return ff.isEnabled;
}

export function useBetaFeatureWeavePythonEcosystem(): boolean {
  return useBetaFeature('weave-python-ecosystem').isEnabled;
}

export function useBetaFeatureOldLaunchUI(): boolean {
  return useBetaFeature('launch-ui-old').isEnabled;
}

function useBetaFeaturePanelSearchBeta(): boolean {
  return useBetaFeature('panel-search-beta').isEnabled;
}

export function usePanelSearchBeta(entityName: string): boolean {
  const isSettingEnabled = useBetaFeaturePanelSearchBeta();
  const isFeatureFlagEnabled = useFeatureFlagPanelSearchBeta(entityName);
  return isSettingEnabled || isFeatureFlagEnabled;
}

function useViewerIsWandbEmployee() {
  const viewer = useViewer();
  if (viewer == null) {
    return false;
  }
  return (
    envIsPublicCloud &&
    (viewer.admin ||
      viewer.email.endsWith('@wandb.ai') ||
      viewer.email.endsWith('@wandb.com'))
  );
}

export function useBetaFeature(featureKey: BetaFeatureKey): BetaFeature {
  // Migrate the user's feature flags from their bio to viewer.userInfo.betaFeatures (if we haven't done so already)
  useBetaFeaturesMigration();

  const viewerUserInfo = useViewerUserInfoQuery();
  const viewerIsWandbEmployee = useViewerIsWandbEmployee();
  const userInfo = viewerUserInfo?.data?.viewer?.userInfo;
  const userInfoBetaFeatures: UserInfo['betaFeatures'] = userInfo?.betaFeatures;

  const [updateUserInfo] = useUpdateUserInfoMutation();

  const enableForCI =
    envIsIntegration &&
    featureKey !== 'weave-devpopup' &&
    // Please do not change this flag without talking to the Weave team.
    (featureKey !== 'weave1' || !envIsLocal) &&
    featureKey !== 'unicorn-plot' &&
    featureKey !== 'lazy-table';

  let userSelectedValue = userInfoBetaFeatures?.[featureKey];

  // If the user is a wandb employee, then we enable these features by default.
  // However, they should be able to explicitly disable it, so we only enable it if the user hasn't set a value.
  if (
    viewerIsWandbEmployee &&
    userSelectedValue === undefined &&
    (featureKey === 'weave1' ||
      featureKey === 'panel-search-beta' ||
      featureKey === 'copy-chart-legend-data')
  ) {
    userSelectedValue = true;
  }

  const isEnabled = (enableForCI || userSelectedValue) ?? false;

  const toggleBetaFeature = useCallback(() => {
    if (userInfo == null) {
      return;
    }
    if (!isEnabled) {
      Analytics.track(`User enabled beta feature ${featureKey}`, {featureKey});
    }

    updateUserInfo({
      variables: {
        userInfo: JSON.stringify({
          ...userInfo,
          betaFeatures: {
            ...userInfoBetaFeatures,
            [featureKey]: !isEnabled,
          },
        }),
      },
    });
  }, [featureKey, isEnabled, updateUserInfo, userInfo, userInfoBetaFeatures]);

  return useMemo(
    () => ({isEnabled, toggleBetaFeature, userSelectedValue}),
    [isEnabled, toggleBetaFeature, userSelectedValue]
  );
}

// Beta features used to be enabled by the presence of keywords in viewer.bio.
// Now we store flags in viewer.userInfo.betaFeatures.
// This hook returns a function that migrates viewer.bio feature keywords to viewer.userInfo.betaFeatures
// Note: it does *not* delete the keywords from viewer.bio
function useBetaFeaturesMigration(): void {
  const viewer = useViewer();
  const [updateUserInfo] = useUpdateUserInfoMutation();

  const userInfoBetaFeatures = viewer?.userInfo?.betaFeatures;

  const migrateBetaFeatures = useCallback(() => {
    if (viewer == null || viewingAs()) {
      return;
    }

    // No-op if we've already migrated
    // TODO: this will infinite loop if the update fails
    if (userInfoBetaFeatures != null) {
      return;
    }

    // Search the user's bio for beta feature keywords
    const bioBetaFeatures: {[k: string]: boolean} = {};
    for (const key of ALL_BETA_FEATURE_KEYS) {
      bioBetaFeatures[key] =
        viewer?.userInfo?.bio?.toLowerCase()?.includes(key) ?? false;
    }

    // Copy bio-enabled features to viewer.userInfo.betaFeatures
    updateUserInfo({
      variables: {
        userInfo: JSON.stringify({
          ...viewer.userInfo,
          betaFeatures: bioBetaFeatures,
        }),
      },
    }).catch(e => {
      console.log(e);
    });
  }, [updateUserInfo, userInfoBetaFeatures, viewer]);

  useEffect(migrateBetaFeatures, [migrateBetaFeatures]);
}
